import React from "react";
import { Box, Skeleton } from "@mui/material";

interface ChartSkeletonProps {
  variant?: "line" | "bar" | "doughnut" | "horizontal-bar";
  width?: number | string;
  height?: number | string;
}

const ChartSkeleton: React.FC<ChartSkeletonProps> = ({
  variant = "line",
  width = "100%",
  height = 300,
}) => {
  return (
    <Box
      sx={{
        width: width,
        height: height,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: 3,
        backgroundColor: "#f8f9fa",
        borderRadius: "16px",
        border: "1px solid #e0e0e0",
      }}
    >
      {variant === "line" && (
        <Box sx={{ width: "100%", height: "100%" }}>
          <Skeleton
            variant="text"
            width="40%"
            height={30}
            sx={{ marginBottom: 2 }}
          />
          <Box sx={{ display: "flex", alignItems: "flex-end", height: "80%" }}>
            {[...Array(8)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{
                  width: `${100 / 8}%`,
                  height: `${Math.random() * 60 + 40}%`,
                  marginRight: 1,
                  borderRadius: "4px",
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {variant === "bar" && (
        <Box sx={{ width: "100%", height: "100%" }}>
          <Skeleton
            variant="text"
            width="40%"
            height={30}
            sx={{ marginBottom: 2 }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              height: "80%",
              gap: 2,
            }}
          >
            {[...Array(6)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{
                  width: `${100 / 6}%`,
                  height: `${Math.random() * 60 + 40}%`,
                  borderRadius: "8px",
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {variant === "horizontal-bar" && (
        <Box sx={{ width: "100%", height: "100%" }}>
          <Skeleton
            variant="text"
            width="40%"
            height={30}
            sx={{ marginBottom: 2 }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
              height: "80%",
              gap: 2,
            }}
          >
            {[...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{
                  width: `${Math.random() * 40 + 50}%`,
                  height: "15%",
                  borderRadius: "8px",
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {variant === "doughnut" && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Skeleton
            variant="circular"
            width={Math.min(
              typeof width === "number" ? width * 0.6 : 200,
              typeof height === "number" ? height * 0.6 : 200
            )}
            height={Math.min(
              typeof width === "number" ? width * 0.6 : 200,
              typeof height === "number" ? height * 0.6 : 200
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default ChartSkeleton;
